import type { ReactNode } from "react";
import { ChevronDown, ExternalLink, Eye, MoreVertical, Paperclip } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { formatDate } from "@/components/prosthesis/prosthesis-meta";
import { PrazoComAtraso } from "../PrazoComAtraso";
import { ProductionStatusBadge } from "../ProductionStatusControl";
import { serviceLabel, type ServiceRow } from "../production-types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";

type Props = {
  row: ServiceRow;
  selecionado: boolean;
  onAlternarSelecao: (serviceId: string) => void;
  expandido: boolean;
  /** Ausente = card sempre aberto, sem alavanca própria (é como ele vive dentro do agrupamento). */
  onAlternarExpansao?: () => void;
  /** Dentro do card de pedido, paciente e clínica já estão no cabeçalho do grupo. */
  ocultarPacienteClinica?: boolean;
  onVerServico?: () => void;
  onVerPedido?: () => void;
  onEditarEstagio: () => void;
  onEditarModelo: () => void;
  onEditarResponsavel: () => void;
  onBaixarAnexo: () => void;
  /** Sem `production.manage` o card vira leitura: estágio, modelo e responsável ficam como texto,
   *  e a seleção some (ela só existe pra aplicar em lote). */
  editavel?: boolean;
};

/**
 * Uma linha da Planilha em forma de card, no celular.
 *
 * O que fica sempre à vista é o que se opera no dia a dia: serviço, paciente/clínica, estágio,
 * modelo, prazo e responsável. O resto (data do pedido, dentista, dentes, material, cor,
 * observações, anexo) mora na expansão — continua a um toque, mas não disputa a tela estreita.
 * Estágio, modelo e responsável abrem folha inferior em vez de dropdown miúdo dentro do card.
 */
export function MobilePlanilhaCard({
  row,
  selecionado,
  onAlternarSelecao,
  expandido,
  onAlternarExpansao,
  ocultarPacienteClinica,
  onVerServico,
  onVerPedido,
  onEditarEstagio,
  onEditarModelo,
  onEditarResponsavel,
  onBaixarAnexo,
  editavel = true,
}: Props) {
  const { order, service } = row;
  const titulo = serviceLabel(service);
  const aberto = onAlternarExpansao ? expandido : true;

  return (
    <li
      className={cn(
        "rounded-lg border bg-white dark:bg-card",
        selecionado ? "border-brand-300 dark:border-primary/50" : "border-zinc-200 dark:border-border",
      )}
    >
      <div className="flex items-start gap-1 px-3 pt-3">
        {service && editavel && (
          <label className="-ml-2 -mt-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
            <input
              type="checkbox"
              checked={selecionado}
              onChange={() => onAlternarSelecao(service.id)}
              className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
              aria-label={`Selecionar ${titulo}`}
            />
          </label>
        )}
        <div className="min-w-0 flex-1">
          <span className="flex items-start gap-1.5">
            {service && (
              <span className="mt-0.5 shrink-0">
                <ProductionStatusBadge status={service.status} compact />
              </span>
            )}
            <span className="min-w-0 flex-1 break-words text-sm font-bold leading-snug text-zinc-900 dark:text-foreground">
              {titulo}
            </span>
          </span>
          {!ocultarPacienteClinica && (
            <span className="mt-0.5 block truncate text-[13px] text-zinc-500 dark:text-muted-foreground">
              {order.patientName}
              {!order.organization.isPersonal && ` · ${order.organization.name}`}
            </span>
          )}
        </div>
        {(onVerServico || onVerPedido) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
                aria-label={`Ações de ${titulo}`}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[200px] border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card"
            >
              {service && onVerServico && (
                <DropdownMenuItem
                  onClick={onVerServico}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2.5 text-sm"
                >
                  <Eye className="h-4 w-4 text-zinc-500 dark:text-muted-foreground" />
                  Ver serviço
                </DropdownMenuItem>
              )}
              {onVerPedido && (
                <DropdownMenuItem
                  onClick={onVerPedido}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2.5 text-sm"
                >
                  <ExternalLink className="h-4 w-4 text-zinc-500 dark:text-muted-foreground" />
                  Ver pedido completo
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {service && (
        <div className="mt-2 grid grid-cols-2 gap-2 px-3">
          <CampoEditavel
            rotulo="Estágio"
            valor={service.productionStage?.label ?? "Sem estágio"}
            cor={service.productionStage?.colorHex ?? null}
            editavel={editavel}
            onClick={onEditarEstagio}
          />
          <CampoEditavel
            rotulo="Modelo"
            valor={service.modelStage?.label ?? "Não possui"}
            cor={service.modelStage?.colorHex ?? null}
            editavel={editavel}
            onClick={onEditarModelo}
          />
        </div>
      )}

      <div className="mt-1 flex items-center gap-3 px-3 text-[13px]">
        <span className="flex h-11 shrink-0 items-center text-zinc-600 dark:text-muted-foreground">
          Prazo:&nbsp;
          <PrazoComAtraso rows={[row]} className="font-medium text-zinc-800 dark:text-foreground" />
        </span>
        {service &&
          (editavel ? (
            <button
              type="button"
              onClick={onEditarResponsavel}
              className="flex h-11 min-w-0 flex-1 items-center justify-start gap-1 rounded-md text-left text-zinc-600 transition-colors hover:bg-zinc-50 dark:text-muted-foreground dark:hover:bg-muted"
            >
              <span className="shrink-0">Responsável:</span>
              <span className="min-w-0 truncate font-medium text-zinc-800 dark:text-foreground">
                {service.assignedToUser?.name ?? "—"}
              </span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
            </button>
          ) : (
            <span className="flex h-11 min-w-0 flex-1 items-center gap-1 text-zinc-600 dark:text-muted-foreground">
              <span className="shrink-0">Responsável:</span>
              <span className="min-w-0 truncate font-medium text-zinc-800 dark:text-foreground">
                {service.assignedToUser?.name ?? "—"}
              </span>
            </span>
          ))}
      </div>

      {aberto && (
        <dl className="mx-3 mt-1 space-y-1.5 border-t border-zinc-100 py-2.5 text-[13px] dark:border-border">
          <Linha rotulo="Pedido">{formatDate(order.createdAt)}</Linha>
          <Linha rotulo="Dentista">{order.dentistName ?? "—"}</Linha>
          <Linha rotulo="Dentes">{service?.teeth || "—"}</Linha>
          <Linha rotulo="Material">
            {rotuloDoMaterialDoServico(service) ?? "—"}
          </Linha>
          <Linha rotulo="Cor">{service?.shadeOption?.code ?? "—"}</Linha>
          <Linha rotulo="Observações">{service?.notes || "—"}</Linha>
          <Linha rotulo="Anexo">
            {service?.attachmentFile ? (
              <button
                type="button"
                onClick={onBaixarAnexo}
                className="-my-1 inline-flex h-8 items-center gap-1 font-semibold text-brand-600 hover:text-brand-800 dark:text-primary"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Baixar anexo
              </button>
            ) : (
              "—"
            )}
          </Linha>
        </dl>
      )}

      {onAlternarExpansao ? (
        <button
          type="button"
          onClick={onAlternarExpansao}
          aria-expanded={expandido}
          aria-label={expandido ? "Recolher detalhes" : "Ver detalhes"}
          className="flex h-11 w-full items-center justify-center rounded-b-lg text-zinc-400 transition-colors hover:bg-zinc-50 hover:text-zinc-600 dark:hover:bg-muted"
        >
          <ChevronDown className={cn("h-4 w-4 transition-transform", expandido && "rotate-180")} />
        </button>
      ) : (
        <div className="h-3" />
      )}
    </li>
  );
}

/** Estágio/Modelo: rótulo miúdo em cima + pill colorido tocável que abre a folha de escolha. */
function CampoEditavel({
  rotulo,
  valor,
  cor,
  editavel,
  onClick,
}: {
  rotulo: string;
  valor: string;
  cor: string | null;
  editavel: boolean;
  onClick: () => void;
}) {
  const corpoCls = cn(
    "flex h-11 w-full min-w-0 items-center justify-between gap-1 rounded-md px-2.5 text-[12.5px] font-semibold",
    !cor && "bg-zinc-100 text-zinc-500 dark:bg-muted dark:text-muted-foreground",
  );
  const estilo = cor ? { backgroundColor: `${cor}1a`, color: cor } : undefined;
  return (
    <div className="min-w-0">
      <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
        {rotulo}
      </p>
      {editavel ? (
        <button type="button" onClick={onClick} style={estilo} className={corpoCls}>
          <span className="min-w-0 truncate">{valor}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      ) : (
        <span style={estilo} className={corpoCls}>
          <span className="min-w-0 truncate">{valor}</span>
        </span>
      )}
    </div>
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-zinc-400 dark:text-muted-foreground">{rotulo}</dt>
      <dd className="min-w-0 flex-1 break-words text-zinc-700 dark:text-foreground/80">{children}</dd>
    </div>
  );
}
